import { useEffect, useRef } from 'react';
import { AutoSizer, Collection, WindowScroller } from 'react-virtualized';

import useIsFirstRender from '@/hooks/useIsFirstRender';
import useScreenSize from '@/hooks/useScreenSize';
import ArtworkCard from '@/shared-components/cards/components/ArtworkCard';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';

export default function VirtualList({ items, visibleItems, onLoadMore, navigateToArtwork }) {
  const screenSize = useScreenSize();
  const columnSizesCache = useRef(initColumnSizesCache());
  const sizeAndPositionCache = useRef([]);
  const collectionRef = useRef();
  const isFirstRender = useIsFirstRender();

  function initColumnSizesCache() {
    const columnCount =
   screenSize.width <= 600
     ? 2                // small screens: 2 cards
     : screenSize.width <= 1000
       ? 2              // mid screens (700–1000): 2 cards
       : 3;             // desktop: 3 cards
    const spacer = screenSize.width <= 600 ? 10 : 15;
    // widen desktop: raise max cap and nibble the desktop side gutter
    const widthOfList =
      screenSize.width <= 700
        ? Math.min(550, screenSize.width - 40)
        : screenSize.width <= 1004
          ? screenSize.width - 278
          : Math.min(1050, screenSize.width - 300);
    const columnWidth = (widthOfList - spacer*(columnCount-1)) / columnCount;

    let columnStarts = [];
    let columnHeights = [];

    for(let i=0; i<columnCount; i++) {
      if(i===0) columnStarts[i] = 0;
      else columnStarts[i] = columnStarts[i-1] + columnWidth + spacer;
      columnHeights[i] = 0;
    }

    return { columnCount, columnWidth, spacer, columnStarts, columnHeights };
  }

  function cellSizeAndPositionGetter({ index }) {
    if(!sizeAndPositionCache.current[index]) {
      const aspectRatio = visibleItems[index]?.artwork_media?.artwork_main_picture?.image_original_data?.aspect_ratio ?? 1;
      const heightOfImage = columnSizesCache.current.columnWidth / aspectRatio;
      const heightOfItem = screenSize.width <= 550 ? heightOfImage + 72.5 : screenSize.width <= 1004 ? heightOfImage + 75.5 : heightOfImage + 82;
      const shortestColumn = columnSizesCache.current.columnHeights.indexOf(Math.min(...columnSizesCache.current.columnHeights));
      const item_x = columnSizesCache.current.columnStarts[shortestColumn];
      const item_y = columnSizesCache.current.columnHeights[shortestColumn];
    
      columnSizesCache.current.columnHeights[shortestColumn] += heightOfItem + columnSizesCache.current.spacer;

      const calculatedSizeAndPosition = {
        width: columnSizesCache.current.columnWidth,
        height: heightOfItem,
        x: item_x,
        y: item_y
      };
      sizeAndPositionCache.current[index] = calculatedSizeAndPosition;
      return calculatedSizeAndPosition;
    } else {
      return sizeAndPositionCache.current[index];
    }
  }
    
  useEffect(() => {
    if(!isFirstRender) {
      columnSizesCache.current = initColumnSizesCache();
      sizeAndPositionCache.current = [];
      collectionRef.current.recomputeCellSizesAndPositions();
    }
  }, [screenSize]);

  return (
    <>
      <InfiniteScroll
        onLoadMore={onLoadMore}
        shouldLoadMore={items.data.length < items.totalDocs}
        listLength={visibleItems.length}
      >
        {({ infiniteScrollAssigner }) => (
          <WindowScroller>
            {({ height, scrollTop }) => (
              <AutoSizer disableHeight>
                {({ width }) => (
                  <Collection
                    autoHeight
                    width={width === 0 ? 0.1 : width}
                    height={height}
                    scrollTop={scrollTop}
                    ref={collectionRef}
                    cellCount={visibleItems.length}
                    cellSizeAndPositionGetter={cellSizeAndPositionGetter}
                    className="remove-scrollbar"
                    cellRenderer={({ index, key, style }) => (
                      <div
                        key={key}
                        style={style}
                        ref={infiniteScrollAssigner(index)}
                      >
                        <ArtworkCard
                          artworkData={visibleItems[index]}
                          onClick={() => {
                            navigateToArtwork(visibleItems[index].slug);
                          }}
                        />
                      </div>
                    )}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteScroll>
    </>
  );
}
